import React, { useState, useEffect, useCallback } from "react";
import { X, GitMerge, RefreshCw, ArrowRight } from "lucide-react";
import api from "../utils/api";
import { toast } from "react-toastify";
import { formatIndianCurrency } from "../utils/Const";

const fmt = (n) => `₹${formatIndianCurrency(n || 0)}`;

export default function MergeOrdersModal({ onClose, onMerged }) {
  const [orders, setOrders] = useState([]);
  const [loading, setLoading] = useState(true);
  const [sourceId, setSourceId] = useState(null);
  const [targetId, setTargetId] = useState(null);
  const [merging, setMerging] = useState(false);

  const fetchActive = useCallback(async () => {
    setLoading(true);
    try {
      const res = await api.get("/orders/active");
      const list = [];
      Object.entries(res.data.tables || {}).forEach(([key, t]) => {
        const parts = key.split("-");
        list.push({
          orderId: t.orderId,
          label: `${parts[1]} ${parts[parts.length - 1]}`,
          sub: parts[0],
          total: t.totalAmount || 0,
          orderNumber: t.orderNumber,
          type: "TABLE",
        });
      });
      (res.data.parcels || []).forEach((p) => {
        list.push({
          orderId: p.orderId,
          label: `Parcel ${p.token}`,
          sub: p.customerName || "walk-in",
          total: p.totalAmount || 0,
          orderNumber: p.orderNumber,
          type: "PARCEL",
        });
      });
      setOrders(list);
    } catch {
      toast.error("Failed to load active orders");
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { fetchActive(); }, [fetchActive]);

  const source = orders.find(o => o.orderId === sourceId);
  const target = orders.find(o => o.orderId === targetId);

  const merge = async () => {
    if (!source || !target) return;
    setMerging(true);
    try {
      const res = await api.post(`/orders/${target.orderId}/merge`, { sourceOrderId: source.orderId });
      toast.success(`${source.label} merged into ${target.label}`);
      onMerged && onMerged(res.data.order);
      onClose();
    } catch (err) {
      toast.error(err.response?.data?.message || "Merge failed");
      setMerging(false);
    }
  };

  const OrderPick = ({ title, hint, selectedId, onPick, excludeId }) => (
    <div className="flex-1 min-w-0">
      <p className="text-[10px] font-black uppercase tracking-wider text-slate-400 mb-1.5">{title}</p>
      <p className="text-[10px] text-slate-400 mb-2">{hint}</p>
      <div className="space-y-1.5 max-h-52 overflow-y-auto pr-1">
        {orders.filter(o => o.orderId !== excludeId).map(o => (
          <button
            key={o.orderId}
            onClick={() => onPick(o.orderId)}
            className={`w-full flex items-center gap-2 rounded-xl border px-3 py-2 text-left transition-colors ${selectedId === o.orderId ? "border-fuchsia-400 bg-fuchsia-50 ring-1 ring-fuchsia-300" : "border-slate-200 bg-white hover:border-slate-300"}`}
          >
            <div className="flex-1 min-w-0">
              <p className="text-xs font-bold text-slate-800 truncate">{o.label}</p>
              <p className="text-[10px] text-slate-400 truncate">#{o.orderNumber} · {o.sub}</p>
            </div>
            <span className="text-xs font-black text-slate-700 shrink-0">{fmt(o.total)}</span>
          </button>
        ))}
        {orders.filter(o => o.orderId !== excludeId).length === 0 && (
          <p className="text-[11px] text-slate-400 text-center py-4">No other open orders</p>
        )}
      </div>
    </div>
  );

  return (
    <div className="fixed inset-0 z-[9999] flex items-center justify-center bg-black/50 backdrop-blur-sm p-4">
      <div className="bg-white rounded-2xl shadow-2xl w-full max-w-2xl flex flex-col max-h-[92vh]">
        {/* Header */}
        <div className="flex items-center justify-between px-5 py-4 border-b border-slate-100">
          <div className="flex items-center gap-2">
            <div className="w-8 h-8 rounded-lg bg-fuchsia-500/15 border border-fuchsia-500/25 flex items-center justify-center">
              <GitMerge size={15} className="text-fuchsia-600" />
            </div>
            <div>
              <h2 className="text-sm font-bold text-slate-800">Merge Orders</h2>
              <p className="text-[10px] text-slate-400">Combine two open orders — e.g. tables joining together</p>
            </div>
          </div>
          <button onClick={onClose} className="w-7 h-7 flex items-center justify-center rounded-full hover:bg-slate-100 text-slate-400">
            <X size={14} />
          </button>
        </div>

        <div className="overflow-y-auto flex-1 px-5 py-4">
          {loading ? (
            <div className="flex items-center justify-center py-10">
              <RefreshCw size={18} className="animate-spin text-slate-400" />
            </div>
          ) : orders.length < 2 ? (
            <p className="text-center text-xs text-slate-400 py-10">Need at least two open orders to merge</p>
          ) : (
            <div className="flex gap-4 items-start">
              <OrderPick
                title="From (will be closed)"
                hint="This order's items move out and its table is freed"
                selectedId={sourceId}
                onPick={setSourceId}
                excludeId={targetId}
              />
              <div className="self-center shrink-0 w-8 h-8 rounded-full bg-fuchsia-100 flex items-center justify-center mt-8">
                <ArrowRight size={14} className="text-fuchsia-600" />
              </div>
              <OrderPick
                title="Into (keeps the bill)"
                hint="This order receives all items and stays open"
                selectedId={targetId}
                onPick={setTargetId}
                excludeId={sourceId}
              />
            </div>
          )}

          {source && target && (
            <div className="mt-4 rounded-xl bg-fuchsia-50 border border-fuchsia-100 px-4 py-3">
              <p className="text-xs text-slate-700">
                <strong>{source.label}</strong> ({fmt(source.total)}) will merge into <strong>{target.label}</strong> — new total ≈ <strong>{fmt(source.total + target.total)}</strong>.
                {" "}{source.label}'s table will be freed and a merge note added to the bill.
              </p>
            </div>
          )}
        </div>

        {/* Footer */}
        <div className="px-5 py-3 border-t border-slate-100 flex justify-end gap-2">
          <button onClick={onClose} className="px-4 py-2 text-xs font-semibold bg-slate-100 text-slate-600 rounded-xl hover:bg-slate-200">Cancel</button>
          <button
            onClick={merge}
            disabled={!source || !target || merging}
            className="flex items-center gap-1.5 px-5 py-2 text-xs font-bold bg-fuchsia-600 hover:bg-fuchsia-700 text-white rounded-xl disabled:opacity-40"
          >
            {merging ? <RefreshCw size={12} className="animate-spin" /> : <GitMerge size={12} />}
            Merge Orders
          </button>
        </div>
      </div>
    </div>
  );
}
